import { useEffect, useState } from "react";
import { Zap, TrendingUp, Target, Coins, Droplets, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getPresaleStats, PRESALE_CONFIG, isPresaleActive } from "@/lib/presale";
import { getCachedTotalLp, getCachedUserPurchases } from "@/lib/lpTracker";

export function PresaleStats() {
  const [stats, setStats] = useState(getPresaleStats());
  const [animatedRaised, setAnimatedRaised] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [totalLp, setTotalLp] = useState<number | null>(null);
  const [userPurchases, setUserPurchases] = useState<number | null>(null);
  const [presaleActive, setPresaleActive] = useState(isPresaleActive());

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById("presale-stats");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    // Update stats periodically (in real app, this would be from blockchain)
    const interval = setInterval(() => {
      setStats(getPresaleStats());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Fetch total LP and user purchases
    async function fetchLp() {
      const lp = await getCachedTotalLp();
      const purchases = await getCachedUserPurchases();
      setTotalLp(lp);
      setUserPurchases(purchases);
    }
    fetchLp();

    // Refresh LP every 30 seconds
    const interval = setInterval(fetchLp, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isVisible || totalLp === null) return;

    // Animate raised amount using totalLp
    const duration = 1500;
    const steps = 60;
    const stepValue = totalLp / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += stepValue;
      if (current >= totalLp) {
        setAnimatedRaised(totalLp);
        clearInterval(timer);
      } else {
        setAnimatedRaised(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [totalLp, isVisible]);

  useEffect(() => {
    if (!isVisible || totalLp === null) return;

    // Animate progress bar using totalLp
    const duration = 1500;
    const steps = 60;
    const progress = stats.goal > 0 ? (totalLp / stats.goal) * 100 : 0;
    const stepValue = progress / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += stepValue;
      if (current >= progress) {
        setAnimatedProgress(progress);
        clearInterval(timer);
      } else {
        setAnimatedProgress(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [totalLp, stats.goal, isVisible]);

  useEffect(() => {
    setPresaleActive(isPresaleActive());
    const interval = setInterval(() => {
      setPresaleActive(isPresaleActive());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div id="presale-stats" className="w-full py-12 md:py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Presale Ended Banner */}
        {!presaleActive && (
          <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 mb-6 md:mb-8">
            <CardContent className="pt-4 px-4 md:px-6 pb-4">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-400 flex-shrink-0" />
                <div className="text-center">
                  <p className="text-lg md:text-xl font-bold text-green-400 mb-1">
                    Presale Completed Successfully!
                  </p>
                  <p className="text-sm md:text-base text-green-300">
                    Thank you to all participants. Final statistics are displayed below.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Progress Bar with enhanced design */}
        <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 mb-8 md:mb-12 hover:border-purple-500/40 transition-all duration-300">
          <CardContent className="pt-6 px-4 md:px-6">
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-400" />
                <span className="text-sm md:text-base font-medium text-gray-300">Presale Progress</span>
              </div>
              <span className="text-lg md:text-xl font-bold text-purple-400">
                {totalLp !== null ? animatedProgress.toFixed(1) : "..."}%
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-3 md:h-4 overflow-hidden relative">
              <div
                className="h-full bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 transition-all duration-1500 ease-out relative overflow-hidden"
                style={{ width: `${Math.min(animatedProgress, 100)}%` }}
              >
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent shimmer-animation"
                />
                {/* Glow effect */}
                <div className="absolute inset-0 bg-purple-400/20 blur-sm" />
              </div>
            </div>
            <div className="mt-3 flex justify-between text-xs md:text-sm text-gray-400">
              <span className="font-medium">
                {totalLp !== null ? animatedRaised.toFixed(2) : "..."} SOL
              </span>
              <span className="font-medium">{stats.goal} SOL Goal</span>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid with enhanced cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/20 group">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-purple-400" />
                </div>
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-purple-500/50 group-hover:text-purple-500/70 transition-colors" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                {totalLp !== null ? animatedRaised.toFixed(2) : "..."}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Total LP</div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20 group">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-indigo-500/10 rounded-lg group-hover:bg-indigo-500/20 transition-colors">
                  <Coins className="w-6 h-6 md:w-8 md:h-8 text-indigo-400" />
                </div>
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-indigo-500/50 group-hover:text-indigo-500/70 transition-colors" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                {userPurchases !== null
                  ? ((userPurchases / PRESALE_CONFIG.pricePerToken) / 1000000).toFixed(1)
                  : "..."}M
              </div>
              <div className="text-xs md:text-sm text-gray-400">Tokens Sold</div>
              <div className="mt-2 text-xs text-indigo-400">
                {userPurchases !== null
                  ? `${((userPurchases / PRESALE_CONFIG.pricePerToken) / PRESALE_CONFIG.totalSupply * 100).toFixed(1)}% of supply`
                  : "..."}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/20 group">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                  <Target className="w-6 h-6 md:w-8 md:h-8 text-purple-300" />
                </div>
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-purple-500/50 group-hover:text-purple-500/70 transition-colors" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                {totalLp !== null && stats.goal > 0
                  ? ((stats.goal - totalLp) / stats.goal * 100).toFixed(0)
                  : "..."}%
              </div>
              <div className="text-xs md:text-sm text-gray-400">Remaining</div>
              <div className="mt-2 text-xs text-purple-300">Until hard cap</div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20 group">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-indigo-500/10 rounded-lg group-hover:bg-indigo-500/20 transition-colors">
                  <Droplets className="w-6 h-6 md:w-8 md:h-8 text-indigo-400" />
                </div>
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-indigo-500/50 group-hover:text-indigo-500/70 transition-colors" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                {totalLp !== null ? totalLp.toFixed(2) : "..."}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Total LP</div>
              <div className="mt-2 text-xs text-indigo-400">
                {PRESALE_CONFIG.teamInitialLp} SOL Team Initial LP
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
